import { useState } from "react";
import { UserPlus, ArrowRight, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { FloatingShapes } from "../components/FloatingShapes";
import { Tilt3D } from "../components/Tilt3D";
import { PasswordInput } from "../components/PasswordInput";

export default function Register({ onSwitch }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/register", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err, "Registrazione non riuscita"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center overflow-hidden p-6">
      <FloatingShapes dense />
      <div className="relative z-10 mx-auto w-full max-w-md md:ml-[12vw]">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary shadow-[0_0_32px_rgba(37,99,235,0.5)]">
            <UserPlus className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-xl font-bold">Sales<span className="text-primary">Pailot</span></p>
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Crea il tuo account</p>
          </div>
        </div>

        {done ? (
          <div data-testid="register-success" className="surface space-y-4 p-7 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <h1 className="text-2xl font-bold">Registrazione ricevuta</h1>
            <p className="text-sm text-muted-foreground">
              Il tuo account è in attesa di <strong>approvazione da parte dell'amministratore</strong>.
              Riceverai accesso una volta approvato.
            </p>
            <button
              data-testid="back-to-login-btn"
              onClick={onSwitch}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8]"
            >
              <ArrowLeft className="h-4 w-4" /> Torna all'accesso
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Inizia ora</h1>
            <p className="mt-3 text-sm text-muted-foreground">Crea il tuo spazio personale: contenuti, call e voce solo tuoi.</p>
            <Tilt3D max={6} className="mt-8 rounded-xl">
              <form onSubmit={submit} className="surface space-y-4 p-6">
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground" htmlFor="name">Nome</label>
                  <input id="name" data-testid="register-name-input" type="text" value={form.name} onChange={set("name")}
                    className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-primary" placeholder="Il tuo nome" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground" htmlFor="remail">Email</label>
                  <input id="remail" data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")}
                    className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-primary" placeholder="nome@azienda.com" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground" htmlFor="rpassword">Password</label>
                  <PasswordInput id="rpassword" testId="register-password-input" required minLength={6} value={form.password} onChange={set("password")}
                    placeholder="Almeno 6 caratteri" />
                </div>
                {error && <p data-testid="register-error" className="text-sm text-red-400">{error}</p>}
                <button data-testid="register-submit-btn" type="submit" disabled={loading || !form.email || form.password.length < 6}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-50">
                  {loading ? <Loader2 className="h-4 w-4 spin-slow" /> : <>Crea account <ArrowRight className="h-4 w-4" /></>}
                </button>
                <p className="pt-1 text-center text-sm text-muted-foreground">
                  Hai già un account?{" "}
                  <button type="button" data-testid="go-login-btn" onClick={onSwitch} className="font-semibold text-primary hover:underline">Accedi</button>
                </p>
              </form>
            </Tilt3D>
          </>
        )}
      </div>
    </div>
  );
}
